import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { api } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem('auth_token'));
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('auth_user')); } catch { return null; }
  });
  const [tenant, setTenant] = useState(() => {
    try { return JSON.parse(localStorage.getItem('auth_tenant')); } catch { return null; }
  });
  const [ready, setReady] = useState(false);
  const [planState, setPlanState] = useState(null);

  const applyAuth = useCallback((data) => {
    setToken(data.token);
    setUser(data.user);
    setTenant(data.tenant);
    localStorage.setItem('auth_token', data.token);
    localStorage.setItem('auth_user', JSON.stringify(data.user));
    localStorage.setItem('auth_tenant', JSON.stringify(data.tenant));
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.login({ email, password });
    applyAuth(res);
    return res;
  }, [applyAuth]);

  const signup = useCallback(async (body) => {
    const res = await api.signup(body);
    applyAuth(res);
    return res;
  }, [applyAuth]);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setTenant(null);
    setPlanState(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_tenant');
  }, []);

  // Verify token on mount
  useEffect(() => {
    if (!token) { setReady(true); return; }
    api.me(token)
      .then((res) => {
        setUser(res.user || res);
        if (res.tenant) setTenant(res.tenant);
      })
      .catch(() => logout())
      .finally(() => setReady(true));
  }, []); // mount-only: auth bootstrap

  const refreshPlan = useCallback(async () => {
    if (!token) return;
    try { const p = await api.tenantPlan(token); setPlanState(p); } catch { /* silent */ }
  }, [token]);

  // Load plan state
  useEffect(() => { refreshPlan(); }, [refreshPlan]);

  const hasPlan = useCallback((minPlan) => {
    if (!planState) return false;
    const ep = planState.effective_plan;
    const order = ['free', 'basic', 'standard', 'pro', 'premium'];
    return order.indexOf(ep) >= order.indexOf(minPlan);
  }, [planState]);

  return (
    <AuthContext.Provider value={{ token, user, tenant, ready, planState, hasPlan, refreshPlan, login, signup, logout, applyAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
